import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { getClausesByCategory } from "@/data/clauses";
import {
  BookOpen,
  ArrowRight,
  Home as HomeIcon,
  Briefcase,
  Building2,
  FileText,
} from "lucide-react";

export const Route = createFileRoute("/clauses/")({
  head: () => ({
    meta: [
      { title: "Contract Clause Library — Plain-English Explanations" },
      {
        name: "description",
        content:
          "Free plain-English explanations of common lease, NDA, and freelance contract clauses. Spot red flags before you sign.",
      },
      { property: "og:title", content: "Contract Clause Library — legaldecoder" },
      {
        property: "og:description",
        content:
          "Decode security deposit, non-compete, IP assignment and more. Sample language, red flags, and negotiation tips.",
      },
      { property: "og:url", content: "https://legaldecoder.io/clauses" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/clauses" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          url: "https://legaldecoder.io/clauses",
          name: "Contract Clause Library",
          description:
            "Plain-English explanations of common lease, NDA, employment, and freelance contract clauses.",
          isPartOf: { "@type": "WebSite", name: "legaldecoder", url: "https://legaldecoder.io/" },
        }),
      },
    ],
  }),
  component: ClausesIndex,
});

function ClausesIndex() {
  const lease = getClausesByCategory("lease");
  const work = getClausesByCategory("work");
  const employment = getClausesByCategory("employment");
  const general = getClausesByCategory("general");

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 bg-gradient-glow" aria-hidden />
          <div className="relative mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground">
              <BookOpen className="h-3.5 w-3.5" /> Free clause library
            </div>
            <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
              Contract Clauses, Translated to Plain English
            </h1>
            <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
              Every clause in your lease, NDA, employment agreement, or freelance contract has a
              hidden meaning. We explain what each one really does, what to watch for, and how to
              negotiate.
            </p>
          </div>
        </section>

        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <CategoryBlock
              title="Lease & Rental Clauses"
              icon={<HomeIcon className="h-5 w-5 text-primary-glow" />}
              items={lease}
            />
            <CategoryBlock
              title="NDA & Freelance Clauses"
              icon={<Briefcase className="h-5 w-5 text-primary-glow" />}
              items={work}
            />
            <CategoryBlock
              title="Employment Clauses"
              icon={<Building2 className="h-5 w-5 text-primary-glow" />}
              items={employment}
            />
            <CategoryBlock
              title="General Contract Clauses"
              icon={<FileText className="h-5 w-5 text-primary-glow" />}
              items={general}
            />

            <div className="mt-16 rounded-2xl border border-border bg-card p-6 text-center shadow-card">
              <p className="text-sm text-muted-foreground">
                Want every clause in <em>your</em> contract decoded in seconds?
              </p>
              <Link
                to="/"
                className="mt-3 inline-flex items-center gap-1 text-base font-semibold text-primary-glow hover:underline"
              >
                Try the contract analyzer <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}

function CategoryBlock({
  title,
  icon,
  items,
}: {
  title: string;
  icon: React.ReactNode;
  items: { slug: string; shortTitle: string; intro: string }[];
}) {
  if (items.length === 0) return null;
  return (
    <>
      <div className="mb-4 mt-12 flex items-center gap-2 first:mt-0">
        {icon}
        <h2 className="text-2xl font-bold">{title}</h2>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {items.map((c) => (
          <ClauseCard key={c.slug} slug={c.slug} title={c.shortTitle} intro={c.intro} />
        ))}
      </div>
    </>
  );
}

function ClauseCard({ slug, title, intro }: { slug: string; title: string; intro: string }) {
  // total clauses count is small; using params with a fixed list
  return (
    <Link
      to="/clauses/$slug"
      params={{ slug }}
      className="group rounded-2xl border border-border bg-card p-5 shadow-card transition hover:border-primary-glow/40 hover:shadow-card-hover"
    >
      <h3 className="text-base font-semibold group-hover:text-primary-glow">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{intro}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-glow">
        Read explainer <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}
